import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, FileDown, Copy, ChevronLeft, ChevronRight, Printer } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import {
  TableColumnVisibilityMenu,
  initialColumnVisibility,
  countVisibleInRange,
  appendSelectedColumnsParam,
} from '@/components/shared/TableColumnVisibilityMenu'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { salesReportService } from '@/services/salesReportService'
import type { SalesReportRow } from '@/modules/inventory/reports/types'

function formatDateForInput(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function formatDisplayDate(dateString?: string) {
  if (!dateString) return '-'
  try {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return dateString
  }
}

function formatQty(n: number) {
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 4 })
}

function formatMoney(n: number) {
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function escapeCsvCell(v: string) {
  if (v.includes('"') || v.includes(',') || v.includes('\n')) {
    return `"${v.replace(/"/g, '""')}"`
  }
  return v
}

const SALES_COLUMN_ORDER = [
  'date',
  'itemCode',
  'itemName',
  'category',
  'subCategory',
  'uom',
  'type',
  'customer',
  'invoiceNo',
  'warehouse',
  'batchCode',
  'soldQuantity',
  'unitCostPrice',
  'unitSalesPrice',
  'totalCostAmount',
  'totalTaxableAmount',
  'totalTaxAmount',
  'discountAmount',
  'totalAmountWithTax',
] as const

export const SalesReport: React.FC = () => {
  const { t } = useTranslation('common')
  const { user } = useAuthStore()
  const selectedBranchId = Number(user?.selectedBranch?.branchId ?? 0)

  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')

  const [rows, setRows] = useState<SalesReportRow[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [colVis, setColVis] = useState<Record<string, boolean>>(() =>
    initialColumnVisibility([...SALES_COLUMN_ORDER]),
  )

  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [totalCount, setTotalCount] = useState(0)
  const [hasSearched, setHasSearched] = useState(false)

  const prevBranchIdRef = useRef(0)

  useEffect(() => {
    const today = new Date()
    const start = new Date(today.getFullYear(), today.getMonth(), 1)
    setFromDate(formatDateForInput(start))
    setToDate(formatDateForInput(today))
  }, [])

  useEffect(() => {
    if (prevBranchIdRef.current !== 0 && prevBranchIdRef.current !== selectedBranchId) {
      setRows([])
      setTotalCount(0)
      setHasSearched(false)
    }
    prevBranchIdRef.current = selectedBranchId
  }, [selectedBranchId])

  const totalPages = useMemo(
    () => (totalCount ? Math.ceil(totalCount / pageSize) : 0),
    [totalCount, pageSize],
  )

  const loadPage = useCallback(
    async (page: number, sizeOverride?: number) => {
      if (!fromDate || !toDate) {
        toast.error(t('reports.salesSelectDates'))
        return
      }

      const size = sizeOverride ?? pageSize
      const fromIso = `${fromDate}T00:00:00.000Z`
      const toIso = `${toDate}T23:59:59.999Z`

      setLoading(true)
      try {
        const res = await salesReportService.getSalesReport({
          fromDate: fromIso,
          toDate: toIso,
          pageNumber: page,
          pageSize: size,
        })
        setRows(res.data)
        setTotalCount(res.totalRecords ?? 0)
        setPageNumber(res.pageNumber ?? page)
        setHasSearched(true)
      } catch (e: unknown) {
        const err = e as { response?: { data?: { message?: string } } }
        toast.error(err.response?.data?.message || t('reports.salesLoadFailed'))
        setRows([])
        setTotalCount(0)
      } finally {
        setLoading(false)
      }
    },
    [fromDate, toDate, pageSize, t],
  )

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows
    const q = searchQuery.toLowerCase()
    return rows.filter((r) => {
      const blob = [
        r.date,
        r.itemCode,
        r.itemName,
        r.category,
        r.subCategory,
        r.uom,
        r.type,
        r.customer,
        r.invoiceNo,
        r.warehouse,
        r.batchCode,
        String(r.soldQuantity),
        String(r.unitCostPrice),
        String(r.unitSalesPrice),
        String(r.totalCostAmount),
        String(r.totalTaxableAmount),
        String(r.totalTaxAmount),
        String(r.discountAmount),
        String(r.totalAmountWithTax),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return blob.includes(q)
    })
  }, [rows, searchQuery])

  const totals = useMemo(() => {
    return filteredRows.reduce(
      (acc, r) => {
        acc.qty += r.soldQuantity || 0
        acc.cost += r.totalCostAmount || 0
        acc.taxable += r.totalTaxableAmount || 0
        acc.tax += r.totalTaxAmount || 0
        acc.discount += r.discountAmount || 0
        acc.grand += r.totalAmountWithTax || 0
        return acc
      },
      { qty: 0, cost: 0, taxable: 0, tax: 0, discount: 0, grand: 0 },
    )
  }, [filteredRows])

  const columnMenu = useMemo(
    () => [
      { id: 'date', label: t('reports.date') },
      { id: 'itemCode', label: t('reports.itemCode') },
      { id: 'itemName', label: t('reports.itemName') },
      { id: 'category', label: t('reports.category') },
      { id: 'subCategory', label: t('reports.subCategory') },
      { id: 'uom', label: t('reports.uom') },
      { id: 'type', label: t('reports.type') },
      { id: 'customer', label: t('reports.customer') },
      { id: 'invoiceNo', label: t('reports.invoiceNo') },
      { id: 'warehouse', label: t('reports.warehouse') },
      { id: 'batchCode', label: t('reports.batchCode') },
      { id: 'soldQuantity', label: t('reports.soldQuantity') },
      { id: 'unitCostPrice', label: t('reports.unitCostPrice') },
      { id: 'unitSalesPrice', label: t('reports.salesUnitSalesPrice') },
      { id: 'totalCostAmount', label: t('reports.salesTotalCostAmount') },
      { id: 'totalTaxableAmount', label: t('reports.salesTotalTaxableAmount') },
      { id: 'totalTaxAmount', label: t('reports.salesTotalTaxAmount') },
      { id: 'discountAmount', label: t('reports.salesDiscountAmount') },
      { id: 'totalAmountWithTax', label: t('reports.salesTotalWithTax') },
    ],
    [t],
  )

  const toggleCol = (id: string) => {
    setColVis((p) => ({ ...p, [id]: !p[id] }))
  }

  const handlePageSizeChange = (v: string) => {
    const n = Number(v)
    setPageSize(n)
    setPageNumber(1)
    if (hasSearched) void loadPage(1, n)
  }

  const handleSearchClick = () => {
    void loadPage(1)
  }

  const handlePrint = () => {
    if (!selectedBranchId) {
      toast.error(t('unsellableInventory.selectBranchForWarehouses'))
      return
    }
    if (!fromDate || !toDate) {
      toast.error(t('reports.salesSelectDates'))
      return
    }
    const params = new URLSearchParams({
      branchId: String(selectedBranchId),
      fromDate,
      toDate,
    })
    appendSelectedColumnsParam(params, colVis)
    window.open(`/inventory/reports/sales/print?${params.toString()}`, '_blank')
  }

  const handleCopy = async () => {
    if (filteredRows.length === 0) {
      toast.error(t('inventoryDispatch.nothingToCopy'))
      return
    }
    const headers = SALES_COLUMN_ORDER.filter((id) => colVis[id]).map(
      (id) => columnMenu.find((c) => c.id === id)?.label || id,
    )
    const lines = filteredRows.map((r) => {
      const cells: string[] = []
      if (colVis.date) cells.push(formatDisplayDate(r.date))
      if (colVis.itemCode) cells.push(r.itemCode || '')
      if (colVis.itemName) cells.push(r.itemName || '')
      if (colVis.category) cells.push(r.category || '')
      if (colVis.subCategory) cells.push(r.subCategory || '')
      if (colVis.uom) cells.push(r.uom || '')
      if (colVis.type) cells.push(r.type || '')
      if (colVis.customer) cells.push(r.customer || '')
      if (colVis.invoiceNo) cells.push(r.invoiceNo || '')
      if (colVis.warehouse) cells.push(r.warehouse || '')
      if (colVis.batchCode) cells.push(r.batchCode || '')
      if (colVis.soldQuantity) cells.push(String(r.soldQuantity ?? ''))
      if (colVis.unitCostPrice) cells.push(String(r.unitCostPrice ?? ''))
      if (colVis.unitSalesPrice) cells.push(String(r.unitSalesPrice ?? ''))
      if (colVis.totalCostAmount) cells.push(String(r.totalCostAmount ?? ''))
      if (colVis.totalTaxableAmount) cells.push(String(r.totalTaxableAmount ?? ''))
      if (colVis.totalTaxAmount) cells.push(String(r.totalTaxAmount ?? ''))
      if (colVis.discountAmount) cells.push(String(r.discountAmount ?? ''))
      if (colVis.totalAmountWithTax) cells.push(String(r.totalAmountWithTax ?? ''))
      return cells.join('\t')
    })
    const text = [headers.join('\t'), ...lines].join('\n')
    try {
      await navigator.clipboard.writeText(text)
      toast.success(t('inventoryDispatch.copied'))
    } catch {
      toast.error(t('reports.salesCopyFailed'))
    }
  }

  const handleCsv = () => {
    if (filteredRows.length === 0) {
      toast.error(t('inventoryDispatch.nothingToExport'))
      return
    }
    const headers = SALES_COLUMN_ORDER.filter((id) => colVis[id]).map(
      (id) => columnMenu.find((c) => c.id === id)?.label || id,
    )
    const csvRows = filteredRows.map((r) => {
      const cells: string[] = []
      if (colVis.date) cells.push(escapeCsvCell(formatDisplayDate(r.date)))
      if (colVis.itemCode) cells.push(escapeCsvCell(r.itemCode || ''))
      if (colVis.itemName) cells.push(escapeCsvCell(r.itemName || ''))
      if (colVis.category) cells.push(escapeCsvCell(r.category || ''))
      if (colVis.subCategory) cells.push(escapeCsvCell(r.subCategory || ''))
      if (colVis.uom) cells.push(escapeCsvCell(r.uom || ''))
      if (colVis.type) cells.push(escapeCsvCell(r.type || ''))
      if (colVis.customer) cells.push(escapeCsvCell(r.customer || ''))
      if (colVis.invoiceNo) cells.push(escapeCsvCell(r.invoiceNo || ''))
      if (colVis.warehouse) cells.push(escapeCsvCell(r.warehouse || ''))
      if (colVis.batchCode) cells.push(escapeCsvCell(r.batchCode || ''))
      if (colVis.soldQuantity) cells.push(escapeCsvCell(String(r.soldQuantity ?? '')))
      if (colVis.unitCostPrice) cells.push(escapeCsvCell(String(r.unitCostPrice ?? '')))
      if (colVis.unitSalesPrice) cells.push(escapeCsvCell(String(r.unitSalesPrice ?? '')))
      if (colVis.totalCostAmount) cells.push(escapeCsvCell(String(r.totalCostAmount ?? '')))
      if (colVis.totalTaxableAmount) cells.push(escapeCsvCell(String(r.totalTaxableAmount ?? '')))
      if (colVis.totalTaxAmount) cells.push(escapeCsvCell(String(r.totalTaxAmount ?? '')))
      if (colVis.discountAmount) cells.push(escapeCsvCell(String(r.discountAmount ?? '')))
      if (colVis.totalAmountWithTax) cells.push(escapeCsvCell(String(r.totalAmountWithTax ?? '')))
      return cells.join(',')
    })
    const headerLine = headers.map(escapeCsvCell).join(',')
    const blob = new Blob([[headerLine, ...csvRows].join('\n')], { type: 'text/csv;charset=utf-8;' })
    const a = document.createElement('a')
    const url = URL.createObjectURL(blob)
    a.href = url
    a.download = `sales-report_${fromDate}_${toDate}.csv`
    a.click()
    URL.revokeObjectURL(url)
    toast.success(t('inventoryDispatch.exported'))
  }

  const tailColumnIds = SALES_COLUMN_ORDER.slice(11)
  const anyTailVisible = tailColumnIds.some((id) => colVis[id])
  const labelColSpan = !anyTailVisible
    ? Math.max(1, countVisibleInRange(colVis, SALES_COLUMN_ORDER, 0, SALES_COLUMN_ORDER.length))
    : Math.max(1, countVisibleInRange(colVis, SALES_COLUMN_ORDER, 0, 11))

  const showEmptyTable = hasSearched && !loading && filteredRows.length === 0

  return (
    <div className="sales-report-root space-y-6 p-6">
      <style>{`
        @media print {
          .sales-report-root .no-print { display: none !important; }
          .sales-report-root { padding: 1rem !important; }
        }
      `}</style>

      <div className="no-print">
        <PageHeader title={t('navigation.reports')} description={t('navigation.salesReport')} />
      </div>

      <Card className="no-print">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl">
            <div className="space-y-2">
              <Label>{t('reports.fromDate')}</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>{t('reports.toDate')}</Label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <Label>{t('reports.search')}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={t('reports.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2 justify-end w-full sm:w-auto">
              <Button onClick={handleSearchClick} disabled={loading}>
                {loading ? t('reports.loading') : t('reports.search')}
              </Button>
              <TableColumnVisibilityMenu columns={columnMenu} visibility={colVis} onToggle={toggleCol} />
              <Button variant="outline" size="sm" onClick={() => void handleCopy()} disabled={filteredRows.length === 0}>
                <Copy className="h-4 w-4 mr-2" />
                {t('reports.copy')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleCsv} disabled={filteredRows.length === 0}>
                <FileDown className="h-4 w-4 mr-2" />
                {t('reports.download')}
              </Button>
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <Printer className="h-4 w-4 mr-2" />
                {t('reports.print')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="no-print border-b px-4 py-2 text-sm text-muted-foreground">
            {t('navigation.reports')} — {t('navigation.salesReport')}
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              <p className="mt-4 text-muted-foreground">{t('reports.loading')}</p>
            </div>
          ) : !hasSearched ? (
            <div className="p-12">
              <EmptyState title={t('navigation.salesReport')} description={t('reports.salesClickSearch')} />
            </div>
          ) : showEmptyTable ? (
            <div className="p-12">
              <EmptyState title={t('reports.stockNoData')} description={t('reports.salesEmptyResult')} />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                      {colVis.date && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.date')}
                        </TableHead>
                      )}
                      {colVis.itemCode && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.itemCode')}
                        </TableHead>
                      )}
                      {colVis.itemName && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.itemName')}
                        </TableHead>
                      )}
                      {colVis.category && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.category')}
                        </TableHead>
                      )}
                      {colVis.subCategory && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.subCategory')}
                        </TableHead>
                      )}
                      {colVis.uom && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.uom')}
                        </TableHead>
                      )}
                      {colVis.type && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.type')}
                        </TableHead>
                      )}
                      {colVis.customer && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.customer')}
                        </TableHead>
                      )}
                      {colVis.invoiceNo && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.invoiceNo')}
                        </TableHead>
                      )}
                      {colVis.warehouse && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.warehouse')}
                        </TableHead>
                      )}
                      {colVis.batchCode && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.batchCode')}
                        </TableHead>
                      )}
                      {colVis.soldQuantity && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.soldQuantity')}
                        </TableHead>
                      )}
                      {colVis.unitCostPrice && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.unitCostPrice')}
                        </TableHead>
                      )}
                      {colVis.unitSalesPrice && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.salesUnitSalesPrice')}
                        </TableHead>
                      )}
                      {colVis.totalCostAmount && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.salesTotalCostAmount')}
                        </TableHead>
                      )}
                      {colVis.totalTaxableAmount && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.salesTotalTaxableAmount')}
                        </TableHead>
                      )}
                      {colVis.totalTaxAmount && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.salesTotalTaxAmount')}
                        </TableHead>
                      )}
                      {colVis.discountAmount && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.salesDiscountAmount')}
                        </TableHead>
                      )}
                      {colVis.totalAmountWithTax && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.salesTotalWithTax')}
                        </TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRows.map((r, idx) => (
                      <TableRow key={`${r.invoiceNo}-${r.batchCode}-${idx}`}>
                        {colVis.date && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {formatDisplayDate(r.date)}
                          </TableCell>
                        )}
                        {colVis.itemCode && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.itemCode || '-'}</TableCell>
                        )}
                        {colVis.itemName && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.itemName || '-'}</TableCell>
                        )}
                        {colVis.category && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.category || '-'}</TableCell>
                        )}
                        {colVis.subCategory && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.subCategory || '-'}</TableCell>
                        )}
                        {colVis.uom && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.uom || '-'}</TableCell>
                        )}
                        {colVis.type && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.type || '-'}</TableCell>
                        )}
                        {colVis.customer && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.customer || '-'}</TableCell>
                        )}
                        {colVis.invoiceNo && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.invoiceNo || '-'}</TableCell>
                        )}
                        {colVis.warehouse && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.warehouse?.trim() || '-'}</TableCell>
                        )}
                        {colVis.batchCode && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.batchCode || '-'}</TableCell>
                        )}
                        {colVis.soldQuantity && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatQty(r.soldQuantity)}</TableCell>
                        )}
                        {colVis.unitCostPrice && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatMoney(r.unitCostPrice)}</TableCell>
                        )}
                        {colVis.unitSalesPrice && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatMoney(r.unitSalesPrice)}</TableCell>
                        )}
                        {colVis.totalCostAmount && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatMoney(r.totalCostAmount)}</TableCell>
                        )}
                        {colVis.totalTaxableAmount && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatMoney(r.totalTaxableAmount)}</TableCell>
                        )}
                        {colVis.totalTaxAmount && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatMoney(r.totalTaxAmount)}</TableCell>
                        )}
                        {colVis.discountAmount && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatMoney(r.discountAmount)}</TableCell>
                        )}
                        {colVis.totalAmountWithTax && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatMoney(r.totalAmountWithTax)}</TableCell>
                        )}
                      </TableRow>
                    ))}
                    <TableRow className="border-t-2 border-primary/20 bg-primary/5 font-bold">
                      <TableCell colSpan={labelColSpan} className="px-4 py-2 text-sm text-center align-middle">
                        {t('reports.total')}
                      </TableCell>
                      {colVis.soldQuantity && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatQty(totals.qty)}</TableCell>
                      )}
                      {colVis.unitCostPrice && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">—</TableCell>
                      )}
                      {colVis.unitSalesPrice && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">—</TableCell>
                      )}
                      {colVis.totalCostAmount && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatMoney(totals.cost)}</TableCell>
                      )}
                      {colVis.totalTaxableAmount && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatMoney(totals.taxable)}</TableCell>
                      )}
                      {colVis.totalTaxAmount && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatMoney(totals.tax)}</TableCell>
                      )}
                      {colVis.discountAmount && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatMoney(totals.discount)}</TableCell>
                      )}
                      {colVis.totalAmountWithTax && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatMoney(totals.grand)}</TableCell>
                      )}
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              <div className="no-print border-t px-4 sm:px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground text-center sm:text-left">
                  {t('reports.salesShowing', {
                    start: totalCount === 0 ? 0 : (pageNumber - 1) * pageSize + 1,
                    end: totalCount === 0 ? 0 : Math.min(pageNumber * pageSize, totalCount),
                    total: totalCount,
                  })}
                </div>
                <div className="flex items-center gap-3 flex-wrap justify-center">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">Page Size:</span>
                    <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void loadPage(Math.max(pageNumber - 1, 1))}
                    disabled={pageNumber <= 1 || loading}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline ml-1">Previous</span>
                  </Button>
                  <div className="text-sm whitespace-nowrap px-2">
                    Page {pageNumber} of {totalPages || 1}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void loadPage(Math.min(pageNumber + 1, totalPages || 1))}
                    disabled={pageNumber >= totalPages || loading || totalPages === 0}
                  >
                    <span className="hidden sm:inline mr-1">Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
